import dayjs from 'dayjs'

import { CLEAR_DATE } from './Constants/Constants'

/**
 * Check if a quote's expiry date is before today (local calendar day).
 * A missing or cleared expiry date never counts as expired.
 * @param {Object} quote - The quote object.
 * @returns {boolean} - Returns true if the quote has expired, otherwise false.
 */
export const isQuoteExpired = (quote) => {
    const expiryDate = quote?.expiryDate
    if (!expiryDate || expiryDate === CLEAR_DATE) return false
    return dayjs(expiryDate).isBefore(dayjs(), 'day')
}

/**
 * Select quote item by id and return warnings.
 * @param {Object} quoteItem - The quote item object.
 * @returns {Array|boolean} - Returns an array of warnings if they exist, otherwise false.
 */
export const quoteItemWarnings = (quoteItem) => {
    return quoteItem?.issues?.warnings?.length ? quoteItem.issues.warnings : false
}

/**
 * Select quote item by id and return errors.
 * @param {Object} quoteItem - The quote item object.
 * @returns {Array|boolean} - Returns an array of errors if they exist, otherwise false.
 */
export const quoteItemErrors = (quoteItem) => {
    return quoteItem?.issues?.errors?.length ? quoteItem.issues.errors : false
}

/**
 * Select quote item by id and return if it is unset.
 * @param {Object} quoteItem - The quote item object.
 * @returns {boolean} - Returns true if the quote item is unset, otherwise false.
 */
export const quoteItemUnset = (quoteItem) => {
    // Check basic required fields
    const basicFieldsUnset =
        !quoteItem?.cuttingTechnologyId || !quoteItem?.materialId || !quoteItem?.thickness || !quoteItem?.sheetId

    // Check if secondary processes have unset required values:
    // - custom-property processes need a non-empty customValue
    // - Quantity processes need an input > 0
    let secondaryProcessUnset = false
    if (quoteItem?.secondaryProcesses?.length) {
        secondaryProcessUnset = quoteItem.secondaryProcesses.some(
            (process) =>
                (process.customPropertyName && !process.customValue) ||
                (process.measureType === 'Quantity' && (!process.input || process.input <= 0))
        )
    }

    // Check if manual folding with numberOfFolds > 0 has a maxFoldLength value
    let foldingUnset = false
    if (quoteItem?.foldingSource === 'Manual' && quoteItem?.numberOfFolds > 0) {
        foldingUnset = !quoteItem.maxFoldLength
    }

    return basicFieldsUnset || secondaryProcessUnset || foldingUnset
}

/**
 * Select quote item by id and return if it is successful.
 * @param {Object} quoteItem - The quote item object.
 * @returns {boolean} - Returns true if the quote item has no warnings or errors, otherwise false.
 */
export const quoteItemSuccess = (quoteItem) => {
    return !quoteItem?.issues?.warnings?.length && !quoteItem?.issues?.errors?.length
}

/**
 * Select quote item by id and return if it is calculated.
 * @param {Object} quoteItem - The quote item object.
 * @returns {boolean} - Returns true if the quote item has itemPrice and linePrice, otherwise false.
 */
export const quoteItemCalculated = (quoteItem) => {
    return Boolean(quoteItem?.itemPrice) && Boolean(quoteItem?.linePrice)
}

/**
 * Check if any quote items have issues (warnings or errors).
 * @param {Array} quoteItems - The array of quote items.
 * @returns {boolean} - Returns true if any quote item has issues, otherwise false.
 */
export const quoteHasIssues = (quoteItems) => {
    if (!quoteItems?.length) return false
    return quoteItems.some((item) =>
        Object.keys(item?.issues).some((key) => {
            if (key !== '$type') {
                return item?.issues[key]?.length
            }
        })
    )
}

/**
 * Check if any quote items are unset.
 * @param {Array} quoteItems - The array of quote items.
 * @returns {boolean} - Returns true if any quote item is unset, otherwise false.
 */
export const quoteHasUnsetItems = (quoteItems) => {
    if (!quoteItems?.length) return false
    return quoteItems.some((item) => quoteItemUnset(item))
}

/**
 * Check if a quote item has been priced by a calculation.
 * @param {Object} quoteItem - The quote item.
 * @returns {boolean} - Returns true if the item has a cut price or an item price.
 */
export const isQuoteItemCalculated = (quoteItem) =>
    // A very short cut can round to a zero cut price; resets zero both prices
    Boolean(quoteItem?.cutPrice) || Boolean(quoteItem?.itemPrice)

/**
 * Count how many quote items have warnings, errors, or are unset.
 * @param {Array} quoteItems - The array of quote items.
 * @returns {Object} - Returns an object with counts of unset, warnings, errors, and success.
 */
export const quoteIssuesCount = (quoteItems) => {
    if (!quoteItems?.length) return false
    return quoteItems.reduce(
        (acc, item) => {
            if (quoteItemUnset(item)) acc.unset += 1
            if (!quoteItemUnset(item) && quoteItemWarnings(item)) acc.warnings += 1
            if (!quoteItemUnset(item) && quoteItemErrors(item)) acc.errors += 1
            if (!quoteItemUnset(item) && quoteItemSuccess(item)) acc.success += 1
            return acc
        },
        { unset: 0, warnings: 0, errors: 0, success: 0 }
    )
}

/**
 * Select quote item by id and check if it has warnings or errors.
 * @param {Object} quoteItem - The quote item object.
 * @returns {boolean} - Returns true if the quote item has issues, otherwise false.
 */
export const quoteItemHasIssues = (quoteItem) => {
    if (!quoteItem?.issues) return false
    return Object.keys(quoteItem?.issues).some((key) => {
        if (key !== '$type') {
            return quoteItem?.issues[key]?.length
        }
    })
}

/**
 * Sort by user-defined index.
 * @param {boolean} isAsc - Sort in ascending order if true, otherwise descending.
 * @returns {Function} - Returns a comparison function for sorting.
 */
const sortByUserDefinedIndex = (isAsc) => (a, b) => {
    return isAsc ? a.index - b.index : b.index - a.index
}

/**
 * Sort by default (creation date).
 * @param {boolean} isAsc - Sort in ascending order if true, otherwise descending.
 * @returns {Function} - Returns a comparison function for sorting.
 */
const sortByDefault = (isAsc) => (a, b) => {
    const dateA = new Date(a.createDateUtc)
    const dateB = new Date(b.createDateUtc)
    return isAsc ? dateA - dateB : dateB - dateA
}

/**
 * Sort by name using natural/alphanumeric ordering (e.g. p1, p2, p10 instead of p1, p10, p2).
 * @param {boolean} isAsc - Sort in ascending order if true, otherwise descending.
 * @returns {Function} - Returns a comparison function for sorting.
 */
const sortByName = (isAsc) => (a, b) => {
    const options = { numeric: true }
    return isAsc ? a.name.localeCompare(b.name, undefined, options) : b.name.localeCompare(a.name, undefined, options)
}

/**
 * Sort by quantity.
 * @param {boolean} isAsc - Sort in ascending order if true, otherwise descending.
 * @returns {Function} - Returns a comparison function for sorting.
 */
const sortByQuantity = (isAsc) => (a, b) => {
    return isAsc ? a.quantity - b.quantity : b.quantity - a.quantity
}

/**
 * Sort by unit price.
 * @param {boolean} isAsc - Sort in ascending order if true, otherwise descending.
 * @returns {Function} - Returns a comparison function for sorting.
 */
const sortByUnitPrice = (isAsc) => (a, b) => {
    return isAsc ? a.itemPrice - b.itemPrice : b.itemPrice - a.itemPrice
}

/**
 * Sort by material thickness.
 * @param {boolean} isAsc - Sort in ascending order if true, otherwise descending.
 * @param {Object} materials - The materials object.
 * @returns {Function} - Returns a comparison function for sorting.
 */
const sortByMaterialThickness = (isAsc, materials) => (a, b) => {
    const materialArray = Object.values(materials)

    const materialA = materialArray.find((material) => material.materialId === a.materialId)
    const materialB = materialArray.find((material) => material.materialId === b.materialId)

    const materialThicknessA = {
        materialName: materialA?.materialName ?? '',
        thickness: a.thickness,
    }
    const materialThicknessB = {
        materialName: materialB?.materialName ?? '',
        thickness: b.thickness,
    }

    if (materialThicknessA.materialName === materialThicknessB.materialName) {
        return isAsc
            ? materialThicknessA.thickness - materialThicknessB.thickness
            : materialThicknessB.thickness - materialThicknessA.thickness
    }

    const options = { numeric: true }
    return isAsc
        ? materialThicknessA.materialName.localeCompare(materialThicknessB.materialName, undefined, options)
        : materialThicknessB.materialName.localeCompare(materialThicknessA.materialName, undefined, options)
}

/**
 * Get the sorting function based on selected criteria and direction.
 * @param {string} selectedCriteria - The selected criteria for sorting.
 * @param {string} selectedDirection - The selected direction for sorting ('ASC' or 'DESC').
 * @param {Object} materials - The materials object.
 * @returns {Function} - Returns the appropriate sorting function.
 */
export const getSortFunction = (selectedCriteria, selectedDirection, materials) => {
    const isAsc = selectedDirection === 'ASC'
    switch (selectedCriteria) {
        case 'Default':
            return sortByDefault(isAsc)
        case 'Name':
            return sortByName(isAsc)
        case 'MaterialThickness':
            return sortByMaterialThickness(isAsc, materials)
        case 'Quantity':
            return sortByQuantity(isAsc)
        case 'UnitPrice':
            return sortByUnitPrice(isAsc)
        default:
            return sortByUserDefinedIndex(isAsc)
    }
}

/**
 * Checks if all items in an array have the same value for a given property.
 * Compares each element's property value against the first element's value.
 *
 * @param {Array<Object>} array - The array of objects to check
 * @param {string} property - The property name to compare across all objects
 * @returns {boolean} True if all items have the same property value, false otherwise
 *
 * @example
 * const items = [
 *   { material: 'Steel', quantity: 5 },
 *   { material: 'Steel', quantity: 10 }
 * ];
 * allItemsHaveSameProperty(items, 'material'); // returns true
 * allItemsHaveSameProperty(items, 'quantity'); // returns false
 */
export const allItemsHaveSameProperty = (array, property) => {
    return array.every((element) => element[property] === array[0][property])
}

/**
 * Filters quote items by a free-text search against the part name and the
 * user-visible Part ID (`partId`).
 *
 * The query is split on whitespace into terms, and an item is kept only when
 * *every* term is a case-insensitive substring of its `name` or its `partId`
 * (terms may match across the two fields and in any order — multi-term AND).
 * The internal record id (`quoteItem.id`, an opaque GUID) is intentionally NOT
 * matched: its hex digits produced phantom matches against numeric queries
 * (QM-3398). An empty, whitespace-only, or nullish query returns the list
 * unchanged so callers can apply it unconditionally. A nullish list yields an
 * empty array.
 *
 * @param {Array<Object>} quoteItems - The quote items to filter.
 * @param {string} [searchQuery] - The search term(s) entered by the user.
 * @returns {Array<Object>} The matching quote items (all items when the query is empty).
 */
export const filterQuoteItemsBySearch = (quoteItems, searchQuery) => {
    if (!Array.isArray(quoteItems)) {
        return []
    }

    const terms = searchQuery?.trim().toLowerCase().split(/\s+/).filter(Boolean) ?? []
    if (terms.length === 0) {
        return quoteItems
    }

    return quoteItems.filter((quoteItem) => {
        const name = quoteItem?.name?.toLowerCase() ?? ''
        const partId = quoteItem?.partId?.toLowerCase() ?? ''
        return terms.every((term) => name.includes(term) || partId.includes(term))
    })
}
